/*
 * カスタム RequireNewPassword コンポーネント
 *
 * AdminCreateUserで作成されたユーザーの初回ログイン時に
 * パスワード変更を求める画面。
 * Amplify UIデフォルトではsignup_attributesに基づきEmail等を
 * 必須入力としてしまうため、パスワードのみの変更に対応する
 * カスタムコンポーネントを使用する。
 */

import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import { Branding } from '../../branding';
import PasswordInputField, { isPasswordMatch, validatePasswordRules } from '../PasswordInputField/PasswordInputField';

const useStyles = makeStyles((theme) => ({
	root: {
		width: 460,
	},
	title: {
		marginBottom: theme.spacing(3),
		fontWeight: 'bold',
	},
	box: {
		marginBottom: theme.spacing(2),
	},
	submitButton: {
		backgroundColor: Branding.secondary,
		color: Branding.white,
		'&:hover': {
			backgroundColor: Branding.secondary,
			opacity: Branding.opacityHover,
		},
	},
}));

const RequireNewPassword = ({ user }) => {
	const classes = useStyles();
	const [newPassword, setNewPassword] = useState({ password: '', showPassword: false });
	const [confirmPassword, setConfirmPassword] = useState({ password: '', showPassword: false });
	const [error, setError] = useState('');

	const passwordValidation = validatePasswordRules(newPassword.password);
	const passwordsMatch = isPasswordMatch(newPassword.password, confirmPassword.password);

	const handleSubmit = async () => {
		if (!passwordValidation.isValid || !passwordsMatch) return;

		try {
			// 電話番号のみユーザーの場合、Amplify SDKがemail必須チェックでエラーにするため
			// requiredAttributesを空にしてバイパスする
			if (user.challengeParam) {
				user.challengeParam.requiredAttributes = [];
			}
			await Auth.completeNewPassword(user, newPassword.password, {});
		} catch (err) {
			console.error('Error completing new password:', err);
			setError(err.message || I18n.get('REQUIRE_NEW_PASSWORD_ERROR'));
		}
	};

	return (
		<div className={classes.root}>
			<Typography variant="h5" className={classes.title}>
				{I18n.get('REQUIRE_NEW_PASSWORD_TITLE')}
			</Typography>

			{error && (
				<Box className={classes.box}>
					<Typography color="error">{error}</Typography>
				</Box>
			)}

			<Box className={classes.box}>
				<PasswordInputField
					label={I18n.get('REQUIRE_NEW_PASSWORD_LABEL')}
					value={newPassword.password}
					showPassword={newPassword.showPassword}
					onChange={(value) => setNewPassword({ ...newPassword, password: value })}
					onToggleShowPassword={() => setNewPassword({ ...newPassword, showPassword: !newPassword.showPassword })}
				/>
				{newPassword.password && !passwordValidation.isValid && (
					<Typography variant="caption" color="error">
						{passwordValidation.messages.join(' / ')}
					</Typography>
				)}
			</Box>

			<Box className={classes.box}>
				<PasswordInputField
					label={I18n.get('REQUIRE_NEW_PASSWORD_CONFIRM_LABEL')}
					value={confirmPassword.password}
					showPassword={confirmPassword.showPassword}
					onChange={(value) => setConfirmPassword({ ...confirmPassword, password: value })}
					onToggleShowPassword={() => setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword })}
				/>
				{confirmPassword.password && !passwordsMatch && (
					<Typography variant="caption" color="error">
						{I18n.get('REQUIRE_NEW_PASSWORD_MISMATCH')}
					</Typography>
				)}
			</Box>

			<Box className={classes.box}>
				<Button
					fullWidth
					variant="contained"
					className={classes.submitButton}
					disabled={!passwordValidation.isValid || !passwordsMatch}
					onClick={handleSubmit}
				>
					{I18n.get('REQUIRE_NEW_PASSWORD_SUBMIT')}
				</Button>
			</Box>
		</div>
	);
};

export default RequireNewPassword;
